import { DataGrid, TooltipV2 } from '@cimplico/components';
import { BanknotesIcon, CalculatorIcon, ExclamationTriangleIcon, SwatchIcon, TagIcon, UserGroupIcon } from '@heroicons/react/20/solid';
import { Link, useSearchParams } from 'react-router-dom';
import Currency, { formatCurrencyAmount } from '../../components/currency/Currency';
import Dropzone from '../../components/dropzone/Dropzone';
import { asyncToast } from '../../components/toast/Toast';
import { useCreateDocument } from '../../queries/use-create-document';
import { NatureEnum } from '../../types/nature';
import { ReconciliationWorksheet, WorksheetWithAccount } from '../../types/worksheet';
import plural from '../../utilities/plural';
import { isAllNullOrUndefined, isNullOrUndefined } from '../new-workpaper/utils';
import WorksheetOutstandingItems from '../../components/worksheet-outstanding-items/WorksheetOutstandingItems';
import Assigned from '../worksheet-v2/top-section/Assigned';
import { WorksheetStatusIcon } from '../worksheet-v2/WorksheetStatusIcon';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';

import FileAttachments from './FileAttachments';
import queryClient from '../../query-client';
import WorksheetVariance from './WorksheetVariance';
import ClosingAmountDiffWarning from './ClosingAmountDiffWarning';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/HoverCard';
import { useFirmContext } from '../../providers/FirmProvider';
import { isReconciliationWorksheet } from '../../utilities/is-worksheet';

type TrialBalanceRowProps = {
    /** The worksheet data to render */
    worksheet: WorksheetWithAccount;
    /** Whether the screen is considered small and to collapse columns */
    smallScreen: boolean;
    /** Id of the worksheet with their file drawer open */
    currentFileDrawer?: string;
    /** Callback to open the file drawer for a worksheet */
    onFileDrawerOpen: (id?: string) => void;
};

/**
 * A row in the trial balance that combines both worksheet and account details.
 * This component gives the user a quick preview into the worksheet without the need to always navigate to its dedicated page.
 *
 * Users can also perform quick actions for the associated worksheet such as:
 * - Quick assign users
 * - Drag and drop files
 * - Preview files [coming soon]
 */
function TrialBalanceRow({ worksheet, smallScreen, currentFileDrawer, onFileDrawerOpen }: TrialBalanceRowProps): JSX.Element {
    const [search, setSearch] = useSearchParams();
    const { readOnly } = useWorkpaperContext();
    const account = worksheet.account;

    // The nature (debit/credit) of the workpaper amount for the current year
    const workpaperNatureTY = isNullOrUndefined(account.workpaperCreditTY) ? NatureEnum.DEBIT : NatureEnum.CREDIT;

    // The nature (debit/credit) of the ledger amount for the current year
    const ledgerNatureTY = isNullOrUndefined(account.ledgerCreditTY) ? NatureEnum.DEBIT : NatureEnum.CREDIT;

    // The current "working" amount (ledger + draft journals)
    const workpaperAmountTY = account.workpaperDebitTY ?? account.workpaperCreditTY;

    // The current amount in the ledger
    const ledgerAmountTY = account.ledgerDebitTY ?? account.ledgerCreditTY;

    // The amount in the ledger for last year
    const ledgerAmountLY = account.ledgerDebitLY ?? account.ledgerCreditLY;

    // The nature (debit/credit) of the ledger amount for the previous year
    const ledgerNatureLY = isNullOrUndefined(account.ledgerCreditLY) ? NatureEnum.DEBIT : NatureEnum.CREDIT;

    const currentAmountsDiffer = workpaperAmountTY !== ledgerAmountTY;

    // Both values have to be non-zero numbers
    const hasCurrentAmounts = workpaperAmountTY && ledgerAmountTY;

    // Checks if the nature has flipped from debit -> credit or credit -> debit
    const naturesDiffer = ledgerNatureTY !== workpaperNatureTY;

    const showLedgerAmount = (hasCurrentAmounts && naturesDiffer) || currentAmountsDiffer;

    const createDocumentQuery = useCreateDocument();

    /**
     * Uploads a list of files to the current worksheet.
     *
     * @param files the list of files to upload
     */
    async function handleFileUpload(files: File[]): Promise<void> {
        const done = asyncToast(`Uploading ${files.length} ${plural(files.length, 'file')}`);
        const uploads = [];
        for (const file of files) {
            const uploadPromise = createDocumentQuery.mutateAsync(
                { file, parentFolderId: worksheet.id },
                {
                    onSuccess: () => {
                        done('success', { title: 'New file uploaded!' });
                    },
                    onError: () => {
                        done('error', { title: 'Failed to upload file' });
                    },
                }
            );
            uploads.push(uploadPromise);
        }
        await Promise.allSettled(uploads);
        queryClient.invalidateQueries([worksheet.workpaperId, 'trial-balance']);
        queryClient.invalidateQueries(['worksheets']);
    }

    const isUnreconciled =
        isReconciliationWorksheet(worksheet) && !isAllNullOrUndefined([worksheet.unreconciledCr, worksheet.unreconciledDr]);

    function handleClick(id: string): void {
        if (search.get('worksheetPreview') === id) {
            search.delete('worksheetPreview');
        } else {
            search.set('worksheetPreview', id);
        }
        setSearch(search);
    }

    const previewId = search.get('worksheetPreview');

    return (
        <Dropzone onDrop={handleFileUpload} disabled={readOnly} fileLimit={30}>
            <DataGrid.Row id={worksheet.id} onClick={handleClick} isActive={previewId === worksheet.id}>
                {/* DETAILS */}
                <div className="py-0.5">
                    <div className="flex gap-2 items-start">
                        <WorksheetStatusIcon status={worksheet.status} smallIcon />
                        <Link
                            to={worksheet.id}
                            className="font-medium text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer leading-4 line-clamp-2"
                        >
                            {worksheet.name}
                        </Link>
                    </div>
                    {/* Supplementary info */}
                    <div className="mt-0.5 flex flex-wrap items-center gap-1">
                        {/* Type */}
                        <div className="flex items-center">
                            <TooltipV2 message="Account type" delayMs={750} position="bottom">
                                <SwatchIcon className="text-gray-500 h-3.5 w-3.5 stroke-2 shrink-0" />
                            </TooltipV2>
                            <label className="text-xs text-gray-600 ml-1">{worksheet.account.type}</label>
                        </div>
                        {/* Report Code */}
                        <div className="flex items-center ml-1">
                            <TooltipV2 message="Report code" delayMs={750} position="bottom">
                                <TagIcon className="text-gray-500 h-3.5 w-3.5 stroke-2 shrink-0" />
                            </TooltipV2>
                            <label className="text-xs text-gray-600 ml-1">{worksheet.account.reportCode}</label>
                        </div>
                        {/* Assigned */}
                        <div className="flex items-center ml-1">
                            <TooltipV2 message="Assigned" delayMs={750} position="bottom">
                                <UserGroupIcon className="text-gray-500 h-3.5 w-3.5 stroke-2 mr-1 shrink-0" />
                            </TooltipV2>
                            <Assigned worksheet={worksheet} size="mini" />
                        </div>
                    </div>
                </div>
                {/* CURRENT AMOUNT */}
                <div className="flex gap-2 items-start justify-between relative">
                    <div className={`flex flex-col items-end ${smallScreen ? 'justify-start h-full' : ''}`}>
                        <Link to={worksheet.id} className="hover:underline">
                            <Currency
                                value={workpaperAmountTY}
                                withParentheses={workpaperNatureTY === NatureEnum.CREDIT}
                                className="text-sm text-gray-900 dark:text-white/[0.87] font-medium"
                            />
                        </Link>

                        {showLedgerAmount && (
                            <div className="flex gap-2 items-center text-gray-600 dark:text-white/[0.87]">
                                <TooltipV2 message="Amount in ledger" position="left">
                                    <BanknotesIcon className="w-3.5 h-3.5" />
                                </TooltipV2>
                                <Link to={worksheet.id} className="hover:underline">
                                    <Currency
                                        value={ledgerAmountTY}
                                        withParentheses={ledgerNatureTY === NatureEnum.CREDIT}
                                        className="text-sm"
                                    />
                                </Link>
                            </div>
                        )}
                    </div>
                    {isUnreconciled && (
                        <div className="h-5 flex items-center absolute -right-6">
                            <UnreconciledHoverCard worksheet={worksheet} />
                        </div>
                    )}
                </div>
                {/* AMOUNT (LY) */}
                <div className={`flex flex-col items-end ${smallScreen ? 'justify-start h-full' : ''}`}>
                    <div className="flex items-center gap-1 relative">
                        <Link to={worksheet.id} className="hover:underline">
                            <Currency
                                value={ledgerAmountLY}
                                withParentheses={ledgerNatureLY === NatureEnum.CREDIT}
                                className="text-sm text-gray-700 dark:text-white/[0.87]"
                            />
                        </Link>
                        <div className="absolute left-full ml-2">
                            <ClosingAmountDiffWarning worksheet={worksheet} />
                        </div>
                    </div>
                    {smallScreen && <VarianceColumn worksheet={worksheet} className="text-sm mt-0.5" />}
                </div>
                {/* VARIANCE */}
                {!smallScreen && <VarianceColumn worksheet={worksheet} className="text-sm" />}
                {/* FILES */}
                {!smallScreen && (
                    <FileAttachments worksheet={worksheet} isOpen={currentFileDrawer === worksheet.id} onOpen={onFileDrawerOpen} />
                )}
                {/* CALCULATOR */}
                {!smallScreen && (
                    <div>
                        {worksheet.calculations.length > 0 && (
                            <TooltipV2 message={`${worksheet.calculations.length} ${plural(worksheet.calculations.length, 'calculation')}`}>
                                <Link to={`${worksheet.id}?tab=calculations`} className="flex items-center">
                                    <span className="text-xs text-gray-600 font-medium">{worksheet.calculations.length}</span>
                                    <CalculatorIcon className="h-5 w-5 text-gray-500" />
                                </Link>
                            </TooltipV2>
                        )}
                    </div>
                )}

                {/* OUTSTANDING */}
                {!smallScreen && <WorksheetOutstandingItems worksheet={worksheet} smallScreen={smallScreen} />}

                {smallScreen && (
                    <div className="flex flex-col gap-1 items-center">
                        {/* FILES */}
                        <FileAttachments worksheet={worksheet} isOpen={currentFileDrawer === worksheet.id} onOpen={onFileDrawerOpen} />
                        {/* OUTSTANDING */}
                        <WorksheetOutstandingItems worksheet={worksheet} smallScreen={smallScreen} />
                    </div>
                )}
            </DataGrid.Row>
        </Dropzone>
    );
}

type VarianceColumnProps = {
    /** The worksheet containing a its variance figures */
    worksheet: WorksheetWithAccount;
    /** Class overrides */
    className?: string;
};

/**
 * Helper component to handle the logic for rendering the variance between the previous and current year amounts.
 */
function VarianceColumn({ worksheet, className }: VarianceColumnProps): JSX.Element {
    const [params, setParams] = useSearchParams();
    function toggleVariance(): void {
        const value = params.get('varianceMode') === 'percent' ? 'amount' : 'percent';
        params.set('varianceMode', value);
        setParams(params);
    }

    return (
        <div className={`flex flex-col items-end ${className}`}>
            <TooltipV2 message="Click the variance figure to swap it between percentage and monetary change.">
                <WorksheetVariance worksheet={worksheet} asPercent={params.get('varianceMode') === 'percent'} onClick={toggleVariance} />
            </TooltipV2>
        </div>
    );
}

type UnreconciledHoverCardProps = {
    worksheet: ReconciliationWorksheet;
};
function UnreconciledHoverCard({ worksheet }: UnreconciledHoverCardProps): JSX.Element {
    const { shortId } = useFirmContext();
    return (
        <HoverCard>
            <HoverCardTrigger>
                <ExclamationTriangleIcon className="w-3.5 h-3.5 text-yellow-600" />
            </HoverCardTrigger>
            <HoverCardContent>
                <p className="text-sm text-contentPrimary font-bold">Worksheet is unreconciled</p>
                <p className="text-xs text-contentSecondary mt-1">The worksheet&apos;s entries do not reconcile to the Current Amount.</p>
                <hr className="my-2" />
                <p className="text-sm text-contentSecondary">Unreconciled by</p>
                <Currency
                    className="text-sm text-contentSecondary font-bold"
                    value={worksheet.unreconciledCr ?? worksheet.unreconciledDr}
                    convertToHighestDenomination
                    withParentheses={!isNullOrUndefined(worksheet.unreconciledCr)}
                />
                <hr className="my-2" />
                <Link
                    to={`/${shortId}/workpapers/${worksheet.workpaperId}/trial-balance/${worksheet.id}`}
                    className="text-sm text-primary font-medium"
                >
                    See worksheet
                </Link>
            </HoverCardContent>
        </HoverCard>
    );
}

export default TrialBalanceRow;
